import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { FiArrowLeft, FiCalendar, FiUser, FiMapPin, FiChevronDown } from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import SmartResultCard from '../components/SmartResultCard';
import LiveTeeTimesToggle from '../components/LiveTeeTimesToggle';
import LiveTeeTimesStatus from '../components/LiveTeeTimesStatus';
import GolfNowLinkModal from '../components/GolfNowLinkModal';
import CourseDetailsCard from '../components/CourseDetailsCard';
import WeatherAlertBanner from '../components/WeatherAlertBanner';
import { createSoloRound, calculateCompositeScore } from '../services/roundService';
import { getWeatherForTimeSlots, RateLimitError } from '../services/weatherService';
import { EMPTY_OPEN_INVITE_VALUE } from '../utils/openInvite';
import { getCourseDetailsByName } from '../services/courseDetailsService';
import { getGolfNowTeeTimes, transformGolfNowToResults, sortResultsByDateTime, formatGolfNowPrice } from '../services/golfNowService';
import { getMyFacilityOverride } from '../services/overridesService';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { trackScheduleRoundSmartSolo } from '../services/analytics';
import { useRewardedGate } from '../contexts/RewardedGateContext';

function SmartScheduleResultsSolo() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { gate } = useRewardedGate();
  const toast = useToast();

  // Get data from navigation state
  const navigationState = location.state || {};
  const {
    results = [],
    dateRange,
    holeCount = 18,
    selectedCourse,
    isSolo = true,
    availabilityData = null,
    openInvite: openInviteFromWizard = null,
  } = navigationState;

  const [expandedResult, setExpandedResult] = useState(null);
  const [courseDetails, setCourseDetails] = useState(null);
  const [loadingCourse, setLoadingCourse] = useState(false);
  const [courseExpanded, setCourseExpanded] = useState(false);

  // GolfNow state
  const [golfnowTeeTimes, setGolfnowTeeTimes] = useState([]);
  const [golfnowLoading, setGolfnowLoading] = useState(false);
  const [golfnowError, setGolfnowError] = useState(null);
  const [golfnowFetched, setGolfnowFetched] = useState(false);

  // Live tee times toggle state
  const [showLiveTeeTimes, setShowLiveTeeTimes] = useState(true);
  const [isCreatingRound, setIsCreatingRound] = useState(false);
  const [openInvite] = useState(openInviteFromWizard || EMPTY_OPEN_INVITE_VALUE);

  // Per-user facility-id override (paste-a-GolfNow-link flow)
  const [facilityOverride, setFacilityOverride] = useState(null);
  const [linkModalOpen, setLinkModalOpen] = useState(false);
  const [linkModalMode, setLinkModalMode] = useState('expecting');

  // The real golf_clubs row id, when the course is a saved club (not custom /
  // free-text). Gates the override flow.
  const golfClubId = useMemo(() => {
    if (selectedCourse && typeof selectedCourse === 'object' && !selectedCourse.is_custom) {
      return selectedCourse.id || null;
    }
    return null;
  }, [selectedCourse]);

  // Effective facility id: the user's personal override wins over the shared club.
  const effectiveFacilityId = useMemo(() => {
    return facilityOverride?.suggested_facility_id ?? selectedCourse?.golfnow_facility_id ?? null;
  }, [facilityOverride, selectedCourse]);

  // Name to show in the live tee-times header (GolfNow's label, else the club's).
  const golfnowCourseName = useMemo(() => {
    if (typeof selectedCourse === 'object' && selectedCourse) {
      return selectedCourse.golfnow_course_name || selectedCourse.course_name || selectedCourse.name || null;
    }
    return typeof selectedCourse === 'string' ? selectedCourse : null;
  }, [selectedCourse]);

  // Load the user's saved override for this club on mount / club change.
  useEffect(() => {
    let active = true;
    if (!golfClubId) { setFacilityOverride(null); return; }
    getMyFacilityOverride(golfClubId).then((row) => {
      if (active) setFacilityOverride(row);
    });
    return () => { active = false; };
  }, [golfClubId]);

  // Check if course has GolfNow integration (via override or the club itself)
  const hasGolfnowIntegration = useMemo(() => {
    return !!effectiveFacilityId;
  }, [effectiveFacilityId]);

  // Determine region from course country_code (ISO 2-letter code)
  const golfnowRegion = useMemo(() => {
    const cc = selectedCourse?.country_code?.toUpperCase();
    if (cc === 'GB' || cc === 'UK') return 'UK';
    if (cc === 'IE') return 'IE';
    if (cc === 'AU') return 'AU';
    if (['DE', 'FR', 'ES', 'IT', 'NL', 'BE', 'AT', 'CH', 'PT'].includes(cc)) return 'EU';
    return 'US';
  }, [selectedCourse]);

  // Debug: Log what we received from navigation
  useEffect(() => {
    if (import.meta.env.DEV) {
      console.log('[SmartScheduleResultsSolo] Navigation state:', {
        hasAvailabilityData: !!availabilityData,
        availabilityDataKeys: availabilityData ? Object.keys(availabilityData) : null,
        userId: user?.id,
        resultsCount: results.length
      });
    }
  }, [availabilityData, user, results]);

  // Display results - use GolfNow tee times when available and toggle is on, otherwise fall back to weather-based results
  const displayResults = useMemo(() => {
    if (hasGolfnowIntegration && showLiveTeeTimes && golfnowTeeTimes.length > 0) {
      const transformed = transformGolfNowToResults(golfnowTeeTimes, {
        holeCount,
        weatherResults: results,
        courseData: selectedCourse,
        targetPlayers: 1, // Solo round = 1 player
        availabilityData: availabilityData, // Pass availability if provided
        selectedMemberIds: user?.id ? [user.id] : [],
        minAvailableMembers: 1 // Solo requires just the one user
      });
      return sortResultsByDateTime(transformed);
    }
    return results;
  }, [hasGolfnowIntegration, showLiveTeeTimes, golfnowTeeTimes, holeCount, results, selectedCourse, availabilityData, user]);

  // Whenever the selected course has coordinates, ensure every displayed slot
  // has weather attached so the score reflects current conditions. See
  // sibling SmartScheduleResults.jsx for the equivalent logic.
  const [weatherByKey, setWeatherByKey] = useState({});
  // Surfaced when the forecast fails to load, so the page explains itself
  // instead of failing silently. Bumping the nonce re-runs enrichment.
  const [weatherError, setWeatherError] = useState(null);
  const [weatherRetryNonce, setWeatherRetryNonce] = useState(0);

  const slotKey = (r) => r.datetime || (r.date && r.window?.start ? `${r.date}T${r.window.start}:00` : null);

  useEffect(() => {
    const lat = selectedCourse?.latitude;
    const lon = selectedCourse?.longitude;
    if (!lat || !lon || !enrichedResults.length) return;
    const needWeather = displayResults.filter(r => !r.weather && slotKey(r) && !weatherByKey[slotKey(r)]);
    if (!needWeather.length) return;
    let cancelled = false;
    (async () => {
      try {
        const slots = needWeather.map(r => ({
          ...r,
          startTime: slotKey(r),
          datetime: slotKey(r),
          holeCount: r.holeCount ?? holeCount,
        }));
        const enriched = await getWeatherForTimeSlots(lat, lon, slots, { durationHours: holeCount === 9 ? 2 : 4 });
        if (cancelled || !Array.isArray(enriched)) return;
        const next = {};
        enriched.forEach(s => {
          const k = s.datetime || s.startTime;
          if (k) next[k] = {
            weather: s.weather || null,
            weatherScore: s.weatherScore || 0,
            weatherError: s.weatherError || null,
            dataSource: s.dataSource || null,
            accuracy: s.accuracy || null,
          };
        });
        if (cancelled) return;
        setWeatherByKey(prev => ({ ...prev, ...next }));
        setWeatherError(null);
      } catch (err) {
        if (cancelled) return;
        console.warn('[SmartScheduleResultsSolo] weather enrichment failed:', err);
        if (err instanceof RateLimitError) {
          setWeatherError({ type: 'rate-limit', message: `${err.message} Your forecast will load once the limit resets.` });
        } else {
          setWeatherError({ type: 'error', message: 'We could not load the forecast right now. Times are still ranked, just without weather.' });
        }
      }
    })();
    return () => { cancelled = true; };
  }, [selectedCourse, displayResults, holeCount, weatherByKey, weatherRetryNonce]);

  const enrichedResults = useMemo(() => {
    const lat = selectedCourse?.latitude;
    const lon = selectedCourse?.longitude;
    return displayResults.map(r => {
      if (r.weather) return r;
      const k = slotKey(r);
      const w = k && weatherByKey[k];
      if (!w || !w.weather) return r;
      const merged = {
        ...r,
        weather: w.weather,
        weatherScore: w.weatherScore,
        weatherError: w.weatherError,
        dataSource: w.dataSource,
        accuracy: w.accuracy,
        holeCount: r.holeCount ?? holeCount,
        golfCourse: {
          ...(r.golfCourse || {}),
          coordinates: r.golfCourse?.coordinates || (lat && lon ? { lat, lon } : null),
          hasWeatherData: !w.weatherError,
        },
      };
      merged.compositeScore = calculateCompositeScore(merged);
      return merged;
    });
  }, [displayResults, weatherByKey, selectedCourse, holeCount]);

  // Calculate minimum price from GolfNow results
  const golfnowMinPrice = useMemo(() => {
    if (!enrichedResults || enrichedResults.length === 0) return null;
    const prices = enrichedResults
      .map(r => r.golfnow?.price)
      .filter(p => p != null && p > 0);
    if (prices.length === 0) return null;
    return {
      price: Math.min(...prices),
      currency: enrichedResults.find(r => r.golfnow?.price > 0)?.golfnow?.currency || 'GBP'
    };
  }, [enrichedResults]);

  // Redirect back to the wizard if no navigation state
  useEffect(() => {
    if (!dateRange || !isSolo) {
      navigate('/create-round/smart/when?who=solo', { replace: true });
    }
  }, [dateRange, isSolo, navigate]);

  // Fetch GolfNow tee times when course has facility ID
  useEffect(() => {
    if (!hasGolfnowIntegration || golfnowFetched) return;

    const fetchGolfNow = async () => {
      setGolfnowLoading(true);
      setGolfnowError(null);

      try {
        // Get date range
        let startDate, endDate;
        if (dateRange?.startDate && dateRange?.endDate) {
          startDate = dateRange.startDate;
          endDate = dateRange.endDate;
        } else if (dateRange?.start && dateRange?.end) {
          startDate = dateRange.start;
          endDate = dateRange.end;
        } else if (results?.length > 0) {
          const dates = results.map(r => r.datetime?.split('T')[0] || r.date).filter(Boolean);
          if (dates.length === 0) return;
          startDate = dates.reduce((a, b) => a < b ? a : b);
          endDate = dates.reduce((a, b) => a > b ? a : b);
        } else {
          return;
        }

        console.log('[SmartScheduleResultsSolo] Fetching GolfNow tee times for facility', effectiveFacilityId);

        const result = await getGolfNowTeeTimes(
          effectiveFacilityId,
          startDate,
          endDate,
          { region: golfnowRegion, players: 1 }
        );

        if (result.success) {
          setGolfnowTeeTimes(result.teeTimes || []);
          console.log('[SmartScheduleResultsSolo] GolfNow tee times fetched:', result.teeTimes?.length, 'tee times');
        } else {
          setGolfnowError(result.error || 'Failed to fetch GolfNow tee times');
          console.warn('[SmartScheduleResultsSolo] GolfNow error:', result.error);
        }
      } catch (error) {
        console.error('[SmartScheduleResultsSolo] Error fetching GolfNow tee times:', error);
        setGolfnowError(error.message);
      } finally {
        setGolfnowLoading(false);
        setGolfnowFetched(true);
      }
    };

    fetchGolfNow();
  }, [hasGolfnowIntegration, dateRange, results, selectedCourse, effectiveFacilityId, golfnowFetched, golfnowRegion, holeCount]);

  // After the user pastes a GolfNow link: adopt their facility id locally and
  // re-run the GolfNow fetch. The shared golf_clubs row is never touched.
  const handleOverrideSubmitted = (newFacilityId) => {
    setFacilityOverride({ suggested_facility_id: newFacilityId, status: 'pending' });
    setGolfnowTeeTimes([]);
    setGolfnowError(null);
    setGolfnowFetched(false);
    setShowLiveTeeTimes(true);
    setLinkModalOpen(false);
  };

  // Extract golf course name helper function
  const getGolfCourseName = () => {
    if (!selectedCourse) return null;
    if (typeof selectedCourse === 'string') {
      return selectedCourse.trim();
    } else if (typeof selectedCourse === 'object' && selectedCourse.name) {
      return selectedCourse.name.trim();
    }
    return null;
  };

  // Check if course is custom
  const isCustomCourse = () => {
    if (typeof selectedCourse === 'object') {
      return selectedCourse.is_custom === true;
    }
    return false;
  };

  // Fetch course details on mount
  useEffect(() => {
    const courseName = getGolfCourseName();
    if (!courseName || isCustomCourse()) {
      setCourseDetails(null);
      return;
    }

    const fetchCourseDetails = async () => {
      setLoadingCourse(true);
      try {
        const details = await getCourseDetailsByName(courseName);
        setCourseDetails(details);
      } catch (error) {
        console.error('Error fetching course details:', error);
        setCourseDetails(null);
      } finally {
        setLoadingCourse(false);
      }
    };

    fetchCourseDetails();
  }, [selectedCourse]);

  // Create solo round from result
  const handleCreateRound = async (result) => {
    if (isCreatingRound) return;

    setIsCreatingRound(true);
    try {
      // iOS rewarded ad-gate: 1 free concurrent upcoming round, then watch an
      // ad or go Pro. No-op on web / for Pro / when an ad is unavailable.
      if (!(await gate('planned_round'))) { setIsCreatingRound(false); return; }

      // Combine date and time window into a datetime string (no trailing Z
      // so it's parsed as local time - appending Z forces UTC and shifts the
      // stored hour by the local offset).
      const timeStart = result.window?.start || '09:00';
      const datetime = `${result.date}T${timeStart}:00`;
      const courseObj = result.golfCourse || selectedCourse;

      const roundData = {
        datetime,
        golfCourse: courseObj || getGolfCourseName(),
        holeCount,
        notes: `Smart scheduled solo round`,
        golfnowBookingUrl: result.golfnow?.bookingUrl || null,
        coursePhone: courseObj?.phone || null,
        courseWebsite: courseObj?.website || null,
        openInvite,
      };

      const soloResult = await createSoloRound(roundData, user);

      // Track solo round creation
      if (user?.id && soloResult?.id) {
        trackScheduleRoundSmartSolo(user.id, soloResult.id);
      }

      toast.success('Solo round created!');
      navigate('/rounds');
    } catch (error) {
      console.error('Error creating solo round:', error);
      toast.error('Failed to create round. Please try again.');
      setIsCreatingRound(false);
    }
  };

  // Handle back navigation — return to the wizard's WHEN step in solo mode
  const handleBack = () => {
    navigate('/create-round/smart/when?who=solo', {
      state: {
        dateRange,
        holeCount,
        selectedCourse
      }
    });
  };

  // If missing essential navigation state, show loading (redirect handled by useEffect)
  if (!dateRange) {
    return (
      <div className="min-h-screen bg-app flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-text-primary mb-4">Loading results...</h1>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-app">
      <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-12">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          {/* Header */}
          <div className="mb-10">
            <button
              onClick={handleBack}
              className="flex items-center text-accent hover:text-accent mb-6 group transition-all duration-200"
            >
              <SafeIcon icon={FiArrowLeft} className="w-5 h-5 mr-2 group-hover:-translate-x-1 transition-transform duration-200" />
              <span className="font-bold">Back to Setup</span>
            </button>

            {/* Desktop: Side-by-side layout */}
            <div className="flex flex-col md:flex-row md:items-start gap-6">
              {/* Left: Title and subtitle */}
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-accent rounded-xl flex items-center justify-center shadow-card">
                    <SafeIcon icon={FiUser} className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h1 className="text-2xl lg:text-3xl font-bold text-text-primary">
                      Solo Round Results
                    </h1>
                    <p className="text-base text-text-secondary font-medium">
                      Smart Schedule
                    </p>
                  </div>
                </div>
                <p className="text-lg md:text-xl text-text-secondary font-medium">
                  {golfnowLoading && showLiveTeeTimes
                    ? 'Loading tee times...'
                    : enrichedResults.length === 0
                    ? 'No optimal times found based on your availability'
                    : hasGolfnowIntegration && showLiveTeeTimes && golfnowTeeTimes.length > 0
                    ? `${enrichedResults.length} tee time${enrichedResults.length !== 1 ? 's' : ''} available${golfnowMinPrice ? ` from ${formatGolfNowPrice(golfnowMinPrice.price, golfnowMinPrice.currency)}` : ''}`
                    : `Found ${enrichedResults.length} hourly slot${enrichedResults.length !== 1 ? 's' : ''}`
                  }
                </p>
              </div>

              {/* Right: Course details card (desktop only) - 50% width with 2-column layout */}
              {getGolfCourseName() && !isCustomCourse() && (
                <div className="hidden md:block flex-1">
                  <div className="bg-card rounded-xl shadow-card border border-border-subtle p-5">
                    <h3 className="text-sm font-semibold text-text-muted uppercase tracking-wide mb-4">{getGolfCourseName()}</h3>
                    {loadingCourse ? (
                      <div className="flex items-center gap-2 py-3">
                        <div className="w-5 h-5 border-2 border-accent border-t-transparent rounded-full animate-spin" />
                        <span className="text-sm text-text-muted">Loading course details...</span>
                      </div>
                    ) : courseDetails ? (
                      <CourseDetailsCard course={courseDetails} compact={false} wideLayout={true} />
                    ) : (
                      <p className="text-sm text-text-muted">Course details not available</p>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Mobile: Collapsible course details */}
            {getGolfCourseName() && !isCustomCourse() && (
              <div className="md:hidden mt-6">
                <button
                  onClick={() => setCourseExpanded(!courseExpanded)}
                  className="w-full bg-card rounded-xl border border-border-subtle p-4 flex items-center justify-between  hover:border-border-subtle transition-colors duration-200"
                >
                  <div className="flex items-center gap-3">
                    <SafeIcon icon={FiMapPin} className="w-5 h-5 text-accent" />
                    <div className="text-left">
                      <span className="font-semibold text-text-primary block">{getGolfCourseName()}</span>
                      <span className="text-xs text-text-muted">{courseExpanded ? 'Tap to hide details' : 'Tap for course details'}</span>
                    </div>
                  </div>
                  <SafeIcon
                    icon={FiChevronDown}
                    className={`w-5 h-5 text-text-muted transform transition-transform duration-200 ${courseExpanded ? 'rotate-180' : ''}`}
                  />
                </button>

                <AnimatePresence>
                  {courseExpanded && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.2 }}
                      className="overflow-hidden"
                    >
                      <div className="bg-card rounded-b-xl border border-t-0 border-border-subtle p-4 ">
                        {loadingCourse ? (
                          <div className="flex items-center gap-2 py-3">
                            <div className="w-5 h-5 border-2 border-accent border-t-transparent rounded-full animate-spin" />
                            <span className="text-sm text-text-muted">Loading course details...</span>
                          </div>
                        ) : courseDetails ? (
                          <CourseDetailsCard course={courseDetails} compact={false} />
                        ) : (
                          <p className="text-sm text-text-muted">Course details not available</p>
                        )}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            )}
          </div>

          {/* Weather-load failure notice */}
          {weatherError && (
            <div className="mb-4">
              <WeatherAlertBanner
                message={weatherError.message}
                onRetry={() => { setWeatherError(null); setWeatherRetryNonce(n => n + 1); }}
              />
            </div>
          )}

          {/* Live tee-times status header + paste-a-link prompt (real clubs only) */}
          {golfClubId && (
            <LiveTeeTimesStatus
              courseName={golfnowCourseName}
              facilityId={effectiveFacilityId}
              region={golfnowRegion}
              bookingDate={displayResults?.[0]?.date || dateRange?.startDate || dateRange?.start}
              hasResults={showLiveTeeTimes && !golfnowLoading && golfnowTeeTimes.length > 0}
              canOverride={!!golfClubId}
              onOpenModal={(mode) => { setLinkModalMode(mode); setLinkModalOpen(true); }}
            />
          )}

          {/* Live Tee Times Toggle - only shown for GolfNow-integrated courses */}
          {hasGolfnowIntegration && (
            <LiveTeeTimesToggle
              enabled={showLiveTeeTimes}
              onChange={() => setShowLiveTeeTimes(!showLiveTeeTimes)}
              loading={golfnowLoading}
              noResults={showLiveTeeTimes && !golfnowLoading && golfnowTeeTimes.length === 0}
            />
          )}

          <GolfNowLinkModal
            isOpen={linkModalOpen}
            onClose={() => setLinkModalOpen(false)}
            golfClubId={golfClubId}
            courseName={golfnowCourseName}
            mode={linkModalMode}
            onSubmitted={handleOverrideSubmitted}
          />

          {/* Results Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="space-y-8"
          >
            {/* Results Container */}
            <div className="bg-transparent md:bg-card md:rounded-xl shadow-none md:shadow-card border-0 md:border md:border-border-subtle p-0 md:p-8">
              {golfnowLoading && showLiveTeeTimes ? (
                <div className="flex flex-col items-center justify-center py-16">
                  <div className="w-12 h-12 border-4 border-accent border-t-transparent rounded-full animate-spin mb-4" />
                  <p className="text-text-secondary font-medium">Fetching available tee times...</p>
                  <p className="text-sm text-text-muted mt-1">Checking GolfNow for real-time availability</p>
                </div>
              ) : enrichedResults.length > 0 ? (
                <div className="space-y-4">
                  {enrichedResults.map((result, index) => (
                    <SmartResultCard
                      key={`${result.datetime}-${index}`}
                      result={result}
                      expanded={expandedResult === index}
                      onToggle={() => setExpandedResult(expandedResult === index ? null : index)}
                      onCreateRound={handleCreateRound}
                      isCreatingRound={isCreatingRound}
                      isSolo={true}
                    />
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <div className="w-24 h-24 bg-surface rounded-full flex items-center justify-center mx-auto mb-6">
                    <SafeIcon icon={FiCalendar} className="w-12 h-12 text-text-muted" />
                  </div>
                  <h4 className="text-xl font-bold text-text-primary mb-2">No optimal times found</h4>
                  <p className="text-text-secondary mb-6">
                    Try adjusting your date range or check that you have availability set.
                  </p>
                  <button
                    onClick={handleBack}
                    className="inline-flex items-center px-6 py-3 bg-accent text-white rounded-md hover:bg-accent transition-colors duration-200 font-semibold"
                  >
                    <SafeIcon icon={FiArrowLeft} className="w-4 h-4 mr-2" />
                    Try Different Options
                  </button>
                </div>
              )}
            </div>
          </motion.div>
        </motion.div>
      </div>
    </div>
  );
}

export default SmartScheduleResultsSolo;
